import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { Close, Star, AutoAwesome } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '@mui/material/styles';
import notificationSound from '../../services/notificationSoundService';
import SwipeableNotification from '../Notifications/SwipeableNotification';

const PremiumBadgeNotification = ({ 
  badgeData, 
  onClose, 
  show = true 
}) => {
  const theme = useTheme();
  const [visible, setVisible] = useState(false);
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (show) {
      setVisible(true);
      notificationSound.play('badge');
      const newParticles = Array.from({ length: 12 }, (_, i) => ({
        id: i,
        x: Math.random() * 400,
        y: Math.random() * 300,
        delay: Math.random() * 1.5,
        duration: 1.5 + Math.random() * 1.5
      }));
      setParticles(newParticles);

      const timer = setTimeout(() => {
        handleClose();
      }, 5000);

      return () => clearTimeout(timer);
    } else {
      setVisible(false);
    }
  }, [show]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => onClose?.(), 300);
  };

  if (!badgeData) return null;

  const isDark = theme.palette.mode === 'dark';
  
  const badgeColors = {
    primary: badgeData.color || (isDark ? '#FFD700' : '#1976d2'),
    secondary: badgeData.color ? `${badgeData.color}CC` : (isDark ? '#FFA500' : '#42a5f5'),
    glow: badgeData.color ? `${badgeData.color}40` : (isDark ? 'rgba(255, 215, 0, 0.4)' : 'rgba(25, 118, 210, 0.3)')
  };
  
  const containerStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    backdropFilter: 'blur(10px)',
  };

  const cardStyle = {
    position: 'relative',
    width: '380px',
    maxWidth: '90vw',
    padding: '28px',
    borderRadius: '20px',
    background: isDark 
      ? `linear-gradient(135deg, ${badgeColors.primary}15 0%, ${badgeColors.secondary}08 50%, rgba(0, 0, 0, 0.9) 100%)`
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 255, 0.9) 100%)',
    border: `2px solid ${badgeColors.primary}40`,
    boxShadow: `0 16px 32px ${badgeColors.glow}, 0 0 50px ${badgeColors.primary}15`,
    overflow: 'hidden',
  };

  return (
    <AnimatePresence>
      {show && visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={containerStyle}
        >
          {/* Celebration Particles */}
          {particles.map((particle) => (
            <motion.div
              key={particle.id}
              initial={{ 
                opacity: 0, 
                scale: 0,
                x: particle.x,
                y: particle.y,
                rotate: 0
              }}
              animate={{ 
                opacity: [0, 1, 0], 
                scale: [0, 1, 0],
                y: particle.y - 80,
                rotate: 360
              }}
              transition={{ 
                duration: particle.duration,
                delay: particle.delay,
                ease: "easeOut"
              }}
              style={{
                position: 'absolute',
                width: '6px',
                height: '6px',
                background: badgeColors.primary,
                borderRadius: '50%',
              }}
            />
          ))}

          <SwipeableNotification onDismiss={handleClose} showCloseButton={false}>
            <motion.div
              initial={{ scale: 0, rotate: -90 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0, rotate: 90 }}
              transition={{ 
                type: "spring", 
                stiffness: 180, 
                damping: 18,
                duration: 0.5
              }}
              style={cardStyle}
            >
              {/* Close Button */}
              <IconButton
                onClick={handleClose}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  color: `${badgeColors.primary}B3`,
                  '&:hover': {
                    color: badgeColors.primary,
                  }
                }}
              >
                <Close />
              </IconButton>

            {/* Badge Icon */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mb: 2,
              position: 'relative'
            }}>
              <motion.div
                animate={{ 
                  rotate: [0, 5, -5, 0],
                  scale: [1, 1.05, 1]
                }}
                transition={{ 
                  duration: 1.8,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <Box sx={{
                  width: 70,
                  height: 70,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${badgeColors.primary} 0%, ${badgeColors.secondary} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 6px 24px ${badgeColors.glow}`,
                }}>
                  <Star sx={{ 
                    fontSize: 36, 
                    color: 'white',
                  }} />
                </Box>
              </motion.div>
              
              {/* Floating Stars */}
              {[...Array(2)].map((_, i) => (
                <motion.div
                  key={i}
                  animate={{
                    y: [-8, -16, -8],
                    opacity: [0.4, 1, 0.4],
                    scale: [0.7, 1.1, 0.7]
                  }}
                  transition={{
                    duration: 1.8,
                    delay: i * 0.4,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  style={{
                    position: 'absolute',
                    top: `${18 + i * 12}px`,
                    left: `${90 + i * 25}px`,
                  }}
                >
                  <AutoAwesome sx={{ 
                    color: badgeColors.primary,
                    fontSize: 14 + i * 2
                  }} />
                </motion.div>
              ))}
            </Box>

            {/* Badge Earned Message */}
            <Typography
              variant="h5"
              sx={{
                textAlign: 'center',
                fontWeight: 'bold',
                background: `linear-gradient(135deg, ${badgeColors.primary} 0%, ${badgeColors.secondary} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
                textShadow: `0 2px 8px ${badgeColors.glow}`,
              }}
            >
              Badge Earned!
            </Typography>

            {/* Badge Name */}
            <Typography
              variant="h6"
              sx={{
                textAlign: 'center',
                fontWeight: 600,
                color: badgeColors.primary,
                mb: 1,
              }}
            >
              {badgeData.name}
            </Typography>

            {/* Rarity Badge */}
            {badgeData.rarity && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Box sx={{
                  px: 2,
                  py: 0.5,
                  borderRadius: '10px',
                  background: `${badgeColors.primary}12`,
                  border: `1px solid ${badgeColors.primary}30`,
                  textTransform: 'uppercase',
                  letterSpacing: '0.4px'
                }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 'bold',
                      color: badgeColors.primary,
                      fontSize: '0.7rem'
                    }}
                  >
                    {badgeData.rarity}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Badge Description */}
            <Typography
              variant="body2"
              sx={{
                textAlign: 'center',
                color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)',
                mb: 2,
                lineHeight: 1.4,
              }}
            >
              {badgeData.description || 'You have earned a special badge!'}
            </Typography>

            {/* Points Badge */}
            {badgeData.points > 0 && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <Box sx={{
                  px: 2.5,
                  py: 0.8,
                  borderRadius: '16px',
                  background: `${badgeColors.primary}18`,
                  border: `1px solid ${badgeColors.primary}25`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}>
                  <Star sx={{ 
                    color: badgeColors.primary,
                    fontSize: 16
                  }} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'bold',
                      color: badgeColors.primary,
                    }}
                  >
                    +{badgeData.points} Points
                  </Typography>
                </Box>
              </Box>
            )}
            </motion.div>
          </SwipeableNotification>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Utility function to trigger badge notifications
export const triggerBadgeNotification = (badgeData) => {
  const event = new CustomEvent('badgeEarned', { detail: badgeData });
  window.dispatchEvent(event);
};

export default PremiumBadgeNotification;